import {
  CsProductCardsProduct,
  CsProductCardsSchema,
  ProductDictionary,
  ProductDictionarySchema,
} from 'components/ProductCards/types';
import { AnyLocale, parseAnyLocale } from 'utils/parseAnyLocale';
import { createProductValidator } from '../createProductValidator';
import { createProductCardPanic } from '../createProductCardPanic';

const createDataTransform = (dbs: ProductDictionary) => {
  const validateProduct = createProductValidator(dbs);
  return (data: Record<string, unknown>) => {
    const locale = data.locale;
    const validLocale = parseAnyLocale(locale as AnyLocale);
    const panic = createProductCardPanic(data);
    if (!validLocale) {
      return panic(`${locale} is not a valid locale`);
    }
    const cms = CsProductCardsSchema.safeParse(data);
    if (cms.error) {
      return panic(
        `${data.uid} has invalid schema\n${JSON.stringify(cms.error.format(), null, 2)}`,
      );
    }
    const validSections = cms.data.sections.map((section) => {
      const validatedProducts = section.products.map(validateProduct);
      const sectionErrors = validatedProducts
        .flatMap((product) => product.error)
        .filter(Boolean) as string[];
      const sectionProducts = validatedProducts
        .filter((product) => !product.error)
        .map(({ error: _error, ...product }) => product) as CsProductCardsProduct[];

      return {
        ...section,
        products: sectionProducts,
        errors: sectionErrors,
        productIds: sectionProducts.map((product) => product.product_id),
      };
    });
    const allErrors = validSections.flatMap((section) => section.errors);
    const allProductIds = validSections.flatMap((section) => section.productIds);
    const filteredDbs = ProductDictionarySchema.parse(
      Object.fromEntries(Object.entries(dbs).filter(([id]) => allProductIds.includes(id))),
    );
    const allSections = validSections.map(({ errors: _errors, ...section }) => section);
    return {
      ...cms.data,
      errors: allErrors,
      sections: allSections,
      productIds: allProductIds,
      dbs: filteredDbs,
      locale: validLocale,
    };
  };
};

export default createDataTransform;
